import * as React from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { coachApi } from "./api";
import type { StudyGroupMessage, StudyGroupMessageList, StudyGroupRealtimeEvent } from "./types";

const COACH_MESSAGES_KEY = (groupId: string) =>
  ["learning", "coach", "study-group-messages", groupId] as const;

function wsBaseOrigin(): string {
  const configured = import.meta.env.VITE_API_URL as string | undefined;
  if (configured?.startsWith("http")) {
    const url = new URL(configured);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url.origin;
  }
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}`;
}

export function buildStudyGroupWsUrl(groupId: string, token: string): string {
  return `${wsBaseOrigin()}/api/learning/study-groups/${groupId}/ws?token=${encodeURIComponent(token)}`;
}

/**
 * ``insert: false`` only updates a row already on screen: status, edit and
 * receipt events reach every member, including for messages that member was
 * never shown (blocked or still in moderation).
 */
function mergeMessage(
  list: StudyGroupMessageList,
  incoming: StudyGroupMessage,
  { insert = true }: { insert?: boolean } = {},
): StudyGroupMessageList {
  const byClient = incoming.client_message_id
    ? list.items.findIndex((m) => m.client_message_id === incoming.client_message_id)
    : -1;
  const byId = list.items.findIndex((m) => m.id === incoming.id);
  const index = byClient >= 0 ? byClient : byId;
  if (index >= 0) {
    const items = [...list.items];
    const current = items[index]!;
    // The server strips content from undelivered status events; the author
    // keeps the text they typed.
    const redacted =
      !incoming.is_deleted &&
      (incoming.delivery_status === "failed" || incoming.delivery_status === "processing");
    items[index] = redacted
      ? {
          ...current,
          ...incoming,
          body: current.body,
          image_url: current.image_url,
          link_previews: current.link_previews,
        }
      : { ...current, ...incoming };
    return { ...list, items };
  }
  if (!insert) return list;
  return { items: [incoming, ...list.items], total: list.total + 1 };
}

/** WebSocket subscription for a study-group room (Phase SC-14d). */
export function useStudyGroupRealtime(groupId: string | null, enabled = true) {
  const qc = useQueryClient();
  const wsRef = React.useRef<WebSocket | null>(null);
  const [connected, setConnected] = React.useState(false);
  const [localOnly, setLocalOnly] = React.useState(false);
  const [typingUsers, setTypingUsers] = React.useState<Record<string, string>>({});
  const typingTimers = React.useRef<Record<string, number>>({});

  React.useEffect(() => {
    if (!groupId || !enabled) return;
    const roomId = groupId;

    let ws: WebSocket | null = null;
    let closed = false;
    let retryMs = 800;

    const applyEvent = (event: StudyGroupRealtimeEvent) => {
      if (event.type === "realtime.mode") {
        setLocalOnly(event.mode === "local");
        return;
      }
      if (event.type === "message.deleted") {
        qc.setQueryData<StudyGroupMessageList>(COACH_MESSAGES_KEY(groupId), (prev) => {
          const base = prev ?? { items: [], total: 0 };
          const items = base.items.filter((m) => m.id !== event.message_id);
          const removed = base.items.length - items.length;
          if (removed === 0) return base;
          return {
            ...base,
            items,
            total: Math.max(0, base.total - removed),
          };
        });
        return;
      }
      if (
        event.type === "message.new" ||
        event.type === "message.status" ||
        event.type === "message.updated"
      ) {
        qc.setQueryData<StudyGroupMessageList>(COACH_MESSAGES_KEY(groupId), (prev) => {
          const base = prev ?? { items: [], total: 0 };
          return mergeMessage(base, event.message, { insert: event.type === "message.new" });
        });
      }
      if (event.type === "read.receipt") {
        qc.setQueryData<StudyGroupMessageList>(COACH_MESSAGES_KEY(groupId), (prev) => {
          const base = prev ?? { items: [], total: 0 };
          return mergeMessage(base, event.message, { insert: false });
        });
      }
      if (event.type === "typing.start" && event.display_name) {
        setTypingUsers((prev) => ({ ...prev, [event.user_id]: event.display_name! }));
        window.clearTimeout(typingTimers.current[event.user_id]);
        typingTimers.current[event.user_id] = window.setTimeout(() => {
          setTypingUsers((prev) => {
            const next = { ...prev };
            delete next[event.user_id];
            return next;
          });
        }, 3000);
      }
      if (event.type === "typing.stop") {
        setTypingUsers((prev) => {
          const next = { ...prev };
          delete next[event.user_id];
          return next;
        });
      }
    };

    const scheduleReconnect = () => {
      if (!closed) window.setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 1.6, 8000);
    };

    function connect() {
      if (closed) return;
      // Read per attempt: access tokens rotate, and a reconnect with the
      // original token is rejected once it expires.
      const token = api.getAccessToken();
      if (!token) {
        scheduleReconnect();
        return;
      }
      ws = new WebSocket(buildStudyGroupWsUrl(roomId, token));
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        retryMs = 800;
      };
      ws.onclose = () => {
        setConnected(false);
        setLocalOnly(false);
        scheduleReconnect();
      };
      ws.onmessage = (msg) => {
        try {
          applyEvent(JSON.parse(String(msg.data)) as StudyGroupRealtimeEvent);
        } catch {
          // ignore malformed frames
        }
      };
    }

    connect();

    return () => {
      closed = true;
      setConnected(false);
      setLocalOnly(false);
      setTypingUsers({});
      ws?.close();
      wsRef.current = null;
      Object.values(typingTimers.current).forEach((id) => window.clearTimeout(id));
      typingTimers.current = {};
    };
  }, [groupId, enabled, qc]);

  return { connected, localOnly, typingUsers, wsRef };
}

export function mergeStudyGroupMessageInCache(
  qc: QueryClient,
  groupId: string,
  incoming: StudyGroupMessage,
) {
  qc.setQueryData<StudyGroupMessageList>(COACH_MESSAGES_KEY(groupId), (prev) =>
    mergeMessage(prev ?? { items: [], total: 0 }, incoming),
  );
}

export function patchStudyGroupMessagesCache(
  qc: QueryClient,
  groupId: string,
  updater: (prev: StudyGroupMessageList) => StudyGroupMessageList,
) {
  qc.setQueryData<StudyGroupMessageList>(COACH_MESSAGES_KEY(groupId), (prev) =>
    updater(prev ?? { items: [], total: 0 }),
  );
}

export async function markDeliveredMessagesRead(
  groupId: string,
  messages: StudyGroupMessage[],
  selfUserId: string,
): Promise<StudyGroupMessage[]> {
  const unread = messages.filter(
    (m) =>
      m.author_user_id !== selfUserId &&
      m.delivery_status === "delivered" &&
      !(m.read_by ?? []).some((r) => r.user_id === selfUserId),
  );
  const results = await Promise.all(
    unread.map((m) => coachApi.markStudyGroupMessageRead(groupId, m.id).catch(() => null)),
  );
  return results.filter((m): m is StudyGroupMessage => m !== null);
}
